import React, {useCallback, useEffect, useState} from "react";
import {Button, Col, Input, Modal, Row, Space, Table, Typography} from "antd";
import {useMediaQuery} from "react-responsive";
import {motion} from "framer-motion";
import {CheckCircleOutlined, SearchOutlined, UndoOutlined} from "@ant-design/icons";
import theme from "../theme";
import peopleService from "../services/PeopleService";
import rollCallService from "../services/RollCallService";
import {useSelector} from "react-redux";
import formatDate from "../utils/dateUtils";
import {COT_ROLE, DISTRICT_ROLE, PROMOTER_ROLE, SECTIONAL_ROLE, STATE_ROLE, useUserRole} from "../utils/userSessionUtils";

const {Title, Text} = Typography;

const RESTRICTED_GROUPS = ["refuerzo", "brigadista", "brigadistas"];

const PaseDeListaPage = () => {
    const isMobile = useMediaQuery({maxWidth: 800});
    const auth = useSelector((state) => state.auth || {});
    const currentUsername = auth.user?.username;
    const userRoute = auth.user?.route || [];
    const userGroup = auth.user?.group || "";
    const userDistrictLocal = auth.user?.districtLocal || null;
    const userRole = useUserRole();

    const isRestricted = userRole === COT_ROLE
        && RESTRICTED_GROUPS.includes(userGroup.toLowerCase());

    const filtersByCreatedBy = [PROMOTER_ROLE, SECTIONAL_ROLE].includes(userRole)
        || isRestricted
        || (userRole === DISTRICT_ROLE && userGroup.toLowerCase() !== "estructura");

    const filtersByDistrict = userRole === DISTRICT_ROLE && userGroup.toLowerCase() === "estructura";

    const filtersByState = userRole === STATE_ROLE && userGroup.toLowerCase() === "estructura";

    const [people, setPeople] = useState([]);
    const [rollCallLoading, setRollCallLoading] = useState({});
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10, total: 0});

    const baseFilters = filtersByCreatedBy
        ? {
            name: {value: "", operator: "like"},
            firstLastName: {value: "", operator: "like"},
            secondLastName: {value: "", operator: "like"},
            phone: {value: "", operator: "like"},
            createdBy: {value: currentUsername, operator: "eq"},
          }
        : filtersByDistrict
        ? {
            name: {value: "", operator: "like"},
            firstLastName: {value: "", operator: "like"},
            secondLastName: {value: "", operator: "like"},
            phone: {value: "", operator: "like"},
            districtLocal: {value: userDistrictLocal, operator: "eq"},
          }
        : filtersByState
        ? {
            name: {value: "", operator: "like"},
            firstLastName: {value: "", operator: "like"},
            secondLastName: {value: "", operator: "like"},
            phone: {value: "", operator: "like"},
            entityId: {value: auth.user?.entityId, operator: "eq"},
          }
        : {
            name: {value: "", operator: "like"},
            firstLastName: {value: "", operator: "like"},
            secondLastName: {value: "", operator: "like"},
            phone: {value: "", operator: "like"},
            sectionId: {value: userRoute, operator: "in"},
          };

    const [tempFilters, setTempFilters] = useState({
        name: "",
        firstLastName: "",
        secondLastName: "",
        phone: "",
        sectionId: userRoute,
    });
    const [appliedFilters, setAppliedFilters] = useState(baseFilters);

    const [modal, contextHolder] = Modal.useModal();

    const fetchPeople = useCallback(async (page, pageSize, filters) => {
        setLoading(true);
        try {
            const response = await peopleService.getAll(page, pageSize, filters);
            setPeople(response.data);
            setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize,
                total: response.meta.totalItems,
            }));
        } catch (error) {
            console.error("Error al cargar registros:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPeople(1, 10, appliedFilters).catch(console.error);
    }, [fetchPeople]);

    const handleTableChange = (newPagination) => {
        fetchPeople(newPagination.current, newPagination.pageSize, appliedFilters).catch(console.error);
    };

    const handleFilterChange = (key, value) => {
        setTempFilters((prev) => ({...prev, [key]: value}));
    };

    const applyFilters = () => {
        const textFilters = {
            name: {value: tempFilters.name, operator: "like"},
            firstLastName: {value: tempFilters.firstLastName, operator: "like"},
            secondLastName: {value: tempFilters.secondLastName, operator: "like"},
            phone: {value: tempFilters.phone, operator: "like"},
        };
        const newFilters = filtersByCreatedBy
            ? {...textFilters, createdBy: {value: currentUsername, operator: "eq"}}
            : filtersByDistrict
            ? {...textFilters, districtLocal: {value: userDistrictLocal, operator: "eq"}}
            : filtersByState
            ? {...textFilters, entityId: {value: auth.user?.entityId, operator: "eq"}}
            : {...textFilters, sectionId: {value: tempFilters.sectionId, operator: "in"}};
        setAppliedFilters(newFilters);
        fetchPeople(1, pagination.pageSize, newFilters).catch(console.error);
    };

    const resetFilters = () => {
        setTempFilters({name: "", firstLastName: "", secondLastName: "", phone: "", sectionId: userRoute});
        setAppliedFilters(baseFilters);
        fetchPeople(1, pagination.pageSize, baseFilters).catch(console.error);
    };

    const handleRollCallClick = (record) => {
        const fullName = `${record.name} ${record.firstLastName} ${record.secondLastName || ""}`.trim();
        modal.confirm({
            title: "Confirmar Pase de Lista",
            content: (
                <div>
                    <p>¿Desea marcar asistencia para?</p>
                    <p><strong>{fullName}</strong></p>
                    <p>Sección: <strong>{record.sectionId}</strong></p>
                </div>
            ),
            okText: "Sí, marcar asistencia",
            cancelText: "Cancelar",
            okType: "primary",
            onOk: async () => {
                await handleMarkAttendance(record);
            },
        });
    };

    const handleMarkAttendance = async (record) => {
        const recordId = record.id;
        setRollCallLoading((prev) => ({...prev, [recordId]: true}));
        try {
            const response = await rollCallService.create({
                electorKey: record.electorKey,
                entityId: auth.user?.entityId,
                sectionId: record.sectionId,
            });
            const fullName = `${record.name} ${record.firstLastName} ${record.secondLastName || ""}`.trim();
            modal.success({
                title: "Asistencia marcada exitosamente",
                content: (
                    <div>
                        <p><strong>{fullName}</strong></p>
                        <p>Sección: <strong>{record.sectionId}</strong></p>
                    </div>
                ),
            });
            setPeople((prev) =>
                prev.map((p) =>
                    p.electorKey === response.data.electorKey
                        ? {...p, attendanceAt: response.data.createdAt, attendanceBy: response.data.attendanceBy}
                        : p
                )
            );
        } catch (error) {
            console.error("Error al marcar asistencia:", error);
            let msg = "Error inesperado al marcar asistencia";
            if (error.details && typeof error.details === "string") msg = error.details;
            else if (error.details && Array.isArray(error.details)) msg = error.details.join(", ");
            modal.error({title: "Error al marcar asistencia.", content: msg});
        } finally {
            setRollCallLoading((prev) => ({...prev, [recordId]: false}));
        }
    };


    const actionColumn = {
        title: "Acciones",
        key: "actions",
        render: (record) => (
            <Button
                type={record.attendanceAt ? "default" : "primary"}
                icon={<CheckCircleOutlined/>}
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    handleRollCallClick(record);
                }}
                loading={rollCallLoading[record.id]}
                disabled={!!record.attendanceAt}
            >
                {record.attendanceAt ? "Marcado" : "Pase de Lista"}
            </Button>
        ),
    };

    const limitedColumns = [
        actionColumn,
        {
            title: "Nombre",
            key: "fullName",
            render: (record) =>
                `${record.name} ${record.firstLastName} ${record.secondLastName || ""}`.trim(),
        },
        {title: "Teléfono", dataIndex: "phone", key: "phone"},
    ];

    const useSimpleColumns = isRestricted || [PROMOTER_ROLE, SECTIONAL_ROLE].includes(userRole);

    const columns = useSimpleColumns
        ? limitedColumns
        : [
            actionColumn,
            {
                title: "Nombre",
                key: "fullName",
                render: (record) =>
                    `${record.name} ${record.firstLastName} ${record.secondLastName || ""}`.trim(),
            },
            {title: "Sección", dataIndex: "sectionId", key: "sectionId"},
            {title: "Teléfono", dataIndex: "phone", key: "phone"},
            {
                title: "Pase de lista por",
                dataIndex: "attendanceBy",
                key: "attendanceBy",
                render: (val) => val || "—",
            },
            {
                title: "Fecha de asistencia",
                dataIndex: "attendanceAt",
                key: "attendanceAt",
                render: (val) => (val ? formatDate(val) : "—"),
            },
          ];

    const mobileColumns = [
        {
            title: "Convencido",
            key: "info",
            render: (record) => (
                <div>
                    <strong>{`${record.name} ${record.firstLastName}`}</strong>
                    <div style={{fontSize: "12px", color: "#666"}}>Sección: {record.sectionId}</div>
                    {record.attendanceBy && (
                        <div style={{fontSize: "12px", color: "#666"}}>Lista por: {record.attendanceBy}</div>
                    )}
                </div>
            ),
        },
        {
            title: "Acción",
            key: "actions",
            render: (record) => (
                <Button
                    type={record.attendanceAt ? "default" : "primary"}
                    icon={<CheckCircleOutlined/>}
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRollCallClick(record);
                    }}
                    loading={rollCallLoading[record.id]}
                    disabled={!!record.attendanceAt}
                    block
                >
                    {record.attendanceAt ? "Marcado" : "Pase de Lista"}
                </Button>
            ),
        },
    ];

    const isAvailable = new Date() >= new Date("2026-06-07T00:00:00");

    if (true) {
        return (
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: "500px",
                    margin: "60px auto",
                    background: theme.colors.card,
                    padding: "40px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    textAlign: "center",
                }}
            >
                <Title level={3}>Pase de Lista</Title>
                <p style={{fontSize: "16px", color: "#666"}}>
                    El pase de lista para esta jornada ha concluido
                </p>
            </motion.div>
        );
    }

    return (
        <>
            {contextHolder}
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: isMobile ? "100%" : "90%",
                    width: "90vw",
                    margin: "20px auto",
                    background: theme.colors.card,
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    overflowX: "auto",
                }}
            >
                <Title level={2} style={{textAlign: "center"}}>Pase de Lista</Title>

                {!filtersByCreatedBy && !isRestricted && (
                    <div style={{
                        marginBottom: "16px",
                        padding: "8px 12px",
                        background: "rgba(178, 139, 71, 0.12)",
                        border: "1px solid rgba(178, 139, 71, 0.4)",
                        borderRadius: "6px",
                        fontSize: "13px",
                    }}>
                        Realiza la búsqueda SIN acentos.
                    </div>
                )}

                <Row gutter={[16, 16]} style={{marginBottom: "16px"}}>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Nombre"
                            value={tempFilters.name}
                            onChange={(e) => handleFilterChange("name", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Apellido Paterno"
                            value={tempFilters.firstLastName}
                            onChange={(e) => handleFilterChange("firstLastName", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Apellido Materno"
                            value={tempFilters.secondLastName}
                            onChange={(e) => handleFilterChange("secondLastName", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Teléfono"
                            value={tempFilters.phone}
                            onChange={(e) => handleFilterChange("phone", e.target.value)}
                        />
                    </Col>
                    {!filtersByCreatedBy && !isRestricted && (
                        <Col xs={24} sm={12} md={8}>
                            <Input
                                placeholder="Sección (número)"
                                value={tempFilters.sectionId.join(",")}
                                onChange={(e) => {
                                    const vals = e.target.value
                                        .split(",")
                                        .map((v) => v.trim())
                                        .filter((v) => v !== "" && !isNaN(v))
                                        .map(Number);
                                    handleFilterChange("sectionId", vals);
                                }}
                            />
                        </Col>
                    )}
                </Row>

                <div style={{display: "flex", justifyContent: "flex-end", marginBottom: "20px"}}>
                    <Space>
                        <Button type="primary" icon={<SearchOutlined/>} onClick={applyFilters}>
                            Aplicar Filtros
                        </Button>
                        <Button icon={<UndoOutlined/>} onClick={resetFilters}>
                            Restablecer
                        </Button>
                    </Space>
                </div>

                <Table
                    columns={isMobile ? mobileColumns : columns}
                    dataSource={people}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50"],
                    }}
                    loading={loading}
                    onChange={handleTableChange}
                    scroll={{x: "max-content"}}
                />

                <Text style={{textAlign: "right", display: "block", marginTop: "10px"}}>
                    Mostrando {people.length} de {pagination.total} registros
                </Text>
            </motion.div>
        </>
    );
};

export default PaseDeListaPage;
